import * as React from 'react';
import { useField } from 'formik';
import Error from './Error';
import './scss/input.scss';
import TooltipComponent from '../../TooltipComponent';
import { ISimpleSelectOption } from '../ArrayFields/interfaces';

export type TInputType = 'color' | 'text' | 'number' | 'password' | 'hidden' | 'email';

interface IInputProps {
  id: string;
  label: string;
  name: string;
  type: TInputType;
  placeholder?: string;
  width?: string;
  readonly?: boolean;
  className?: string;
  required?: boolean;
  newStyle?: boolean;
  allowOnlyNumbers?: boolean;
  onChangeCallback?: (value: any) => void;
  onFocusCallback?: () => void;
  onKeyPressCallback?: (value: any) => void;
  onBlurCallBack?: (value: string) => void;
  tooltip?: string;
  step?: number | string;
  warningMessage?: string;
  labelClassName?: string;
  rowClassName?: string;
  columnClassName?: string;
  disabled?: boolean;
  min?: number;
  noDecimal?: boolean;
  isChangeDetail?: string;
  currency?: string;
  options?: ISimpleSelectOption[];
  selectedItems?: string[];
  handleCheckboxesOnChange?: (values: string[]) => void;
  infoMessage?: string;
}

function Input(props: IInputProps): JSX.Element {
  const [field, meta, helpers] = useField(props);
  const [selectedItems, setSelectedItems] = React.useState<string[]>([]);
  const widthValue = props.width ? props.width : 'col-sm-9';
  let errBorder = '';
  if (meta.touched && meta.error) {
    errBorder = 'err-border';
  }

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    const re = /^[+0-9\b]+$/;
    if (value === '' || (props.allowOnlyNumbers && re.test(value))) {
      helpers.setValue(event.currentTarget.value);
      if (props.onChangeCallback) {
        props.onChangeCallback(value);
      }
    }
    if (!props.allowOnlyNumbers) {
      helpers.setValue(event.currentTarget.value);
      if (props.onChangeCallback) {
        props.onChangeCallback(value);
      }
    }
  };

  const handleOnFocus = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (props.onFocusCallback) {
      props.onFocusCallback();
    }
  };

  const handleOnKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    if (props.onKeyPressCallback) {
      props.onKeyPressCallback(value);
    }
  };

  const handleDecimal = (event: any) => {
    if (event.charCode < 48 || event.charCode > 57) {
      event.preventDefault();
      return false;
    }
    return true;
  };

  React.useEffect(() => {
    if (props.selectedItems) {
      setSelectedItems(props.selectedItems);
    }
  }, [props.selectedItems]);

  const oldStyleContent: JSX.Element = (
    <div
      className="form-group text-input-component"
      style={{ marginBottom: props.type === 'hidden' ? '0' : '15px' }}
    >
      <div className="text-left font-weight-bold col-sm-3 control-label">
        <label htmlFor={props.id} className="font-weight-bold">
          {props.required && <span className="text-danger required-field-info">*</span>}
          {props.label}
        </label>
      </div>
      <div className={widthValue}>
        <input
          {...field}
          // {...props}
          data-cy={`form-input-${props.name}`}
          type={props.type}
          placeholder={props.placeholder || ''}
          name={props.name}
          id={props.id}
          autoComplete={props.type === 'password' ? 'off' : 'on'}
          readOnly={props.readonly || false}
          onChange={handleOnChange}
          onFocus={handleOnFocus}
          onKeyPress={handleOnKeyPress}
          className={`${props.className} form-control ${errBorder}`}
          title={props.tooltip || ''}
          step={props.step || undefined}
        />
        {meta.touched && meta.error && <Error message={meta.error} />}
      </div>
    </div>
  );

  const handleOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    helpers.setTouched(true);

    if (props.onBlurCallBack) {
      helpers.setValue(event.currentTarget.value);
      props.onBlurCallBack(field.value);
    }
  };

  const handleCheckboxesOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked, value } = event.currentTarget;
    let values = JSON.parse(JSON.stringify(selectedItems));
    if (checked) {
      values.push(String(value));
      setSelectedItems(values);
    } else {
      values = selectedItems.filter((i: string) => i !== value);
      setSelectedItems(values);
    }
    if (props.handleCheckboxesOnChange) {
      props.handleCheckboxesOnChange(values);
    }
  };

  const newStyleContent: JSX.Element = (
    <div
      className={`input-component form-group ${meta.touched && meta.error && 'form-group-error'}`}
    >
      <div className={props.rowClassName || ''}>
        <label htmlFor={props.id} className={`${props.labelClassName || 'd-block form-label'}`}>
          {props.required && <span className="text-danger required-field-info">*</span>}
          {props.label}
        </label>
        <div className={props.columnClassName || 'd-block'}>
          {props.isChangeDetail ? (
            <div
              style={{
                background: 'rgb(242, 242, 242)',
                border: '1px solid rgb(221, 221, 221)',
                minHeight: '30px',
                color: '#B3B3B3',
                borderRadius: '4px',
                padding: '7px',
              }}
            >
              <TooltipComponent
                id={props.id}
                firstItems={true}
                width="180px"
                positionRight={true}
                overlay={<div style={{ color: '#fff' }}>{props.isChangeDetail}</div>}
              >
                <span
                  style={{
                    background: 'rgb(61, 114, 200)',
                    padding: '2px 10px 3px 10px',
                    borderRadius: '11px',
                    opacity: 0.3,
                    color: 'rgb(0, 0, 0)',
                  }}
                >
                  {field.value}
                </span>
              </TooltipComponent>
            </div>
          ) : (
            <div className={props.currency ? 'form-control-suffix' : props.options ? 'row' : ''}>
              <div className={props.options ? 'inputWithCheckboxes' : ''}>
                <input
                  {...field}
                  // {...props}
                  data-cy={`form-input-${props.name}`}
                  type={props.type}
                  placeholder={props.placeholder || ''}
                  onChange={handleOnChange}
                  name={props.name}
                  id={props.id}
                  readOnly={props.readonly || false}
                  className={`${props.className} form-control ${errBorder}`}
                  title={props.tooltip || ''}
                  step={props.step || undefined}
                  onBlur={handleOnBlur}
                  disabled={!!props.disabled}
                  style={props.disabled ? { cursor: 'not-allowed', color: '#999' } : {}}
                  min={props.min}
                  onKeyPress={props.noDecimal ? handleDecimal : undefined}
                />
                {props.currency && <span className="actions">{props.currency}</span>}
                {props.options && meta.touched && meta.error && (
                  <Error message={meta.error} newStyle={true} />
                )}
                {props.options && props.warningMessage && (
                  <Error message={props.warningMessage} newStyle={true} />
                )}
              </div>
              {props.options && (
                <div
                  className="pull-left"
                  style={{
                    marginTop: '3px',
                    marginBottom: '3px',
                    paddingRight: '15px',
                    paddingLeft: '15px',
                  }}
                >
                  {props.options.map((option: ISimpleSelectOption, index: number) => (
                    <div
                      className="e-checkbox"
                      style={{ marginRight: '8px' }}
                      key={`checkbox-${props.name}-${index}`}
                    >
                      {option.tooltip && (
                        <TooltipComponent
                          id={props.id}
                          firstItems={true}
                          width="180px"
                          positionLeft={true}
                          positionRight={false}
                          top="27px"
                          display="inline"
                          overlay={<div style={{ color: '#fff' }}>{option.tooltip || ''}</div>}
                        >
                          <label data-cy={`multipleCheckboxes-${option.value}`}>
                            <input
                              type="checkbox"
                              disabled={props.disabled || option.disabled || false}
                              value={option.value}
                              onChange={handleCheckboxesOnChange}
                              data-cy={`checkboxGroup-${props.name}-${option.value}`}
                              checked={
                                selectedItems &&
                                selectedItems.length > 0 &&
                                selectedItems.includes(String(option.value))
                              }
                              data-value={option.value}
                              data-disabled={option.disabled}
                            />
                            <span
                              style={{
                                fontSize: '13px',
                                fontFamily: 'Open Sans, sans-serif',
                              }}
                            >
                              {option.item}
                            </span>
                          </label>
                        </TooltipComponent>
                      )}
                      {!option.tooltip && (
                        <label data-cy={`multipleCheckboxes-${option.value}`}>
                          <input
                            type="checkbox"
                            disabled={props.disabled || option.disabled || false}
                            value={option.value}
                            onChange={handleCheckboxesOnChange}
                            data-cy={`checkboxGroup-${props.name}-${option.value}`}
                            checked={
                              selectedItems &&
                              selectedItems.length > 0 &&
                              selectedItems.includes(String(option.value))
                            }
                            data-value={option.value}
                            data-disabled={option.disabled}
                          />
                          <span
                            style={{
                              fontSize: '13px',
                              fontFamily: 'Open Sans, sans-serif',
                            }}
                          >
                            {option.item}
                          </span>
                        </label>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {!props.options && meta.touched && meta.error && (
            <Error message={meta.error} newStyle={true} />
          )}
          {!props.options && props.warningMessage && (
            <Error message={props.warningMessage} newStyle={true} />
          )}
          {!props.options && props.infoMessage && (
            <Error message={props.infoMessage} newStyle={true} isInfo={true} />
          )}
        </div>
      </div>
    </div>
  );

  return props.newStyle ? newStyleContent : oldStyleContent;
}

export default Input;
